import type { PriorityPaymentsResponse } from '@/Network/Types/Responses/PriorityPaymentsResponses'
import { PRIORITY_ROWS_PAGE_LIMIT } from '@/pinia-store/usePriorityPaymentsStore'

import type { PriorityColumnServerFilter, PriorityGroupColumn } from './constants'
import type { PriorityPaymentsFilters } from './filters'

/**
 * Carga por coluna do Kanban: cada coluna busca os primeiros N do SEU grupo
 * (ordem do servidor) e o "Ver mais" traz a próxima página só daquele grupo.
 */
export const PRIORITY_KANBAN_COLUMN_PAGE_SIZE = 25

/**
 * Teto do que um refresh preserva por coluna: uma requisição só, no `limit`
 * máximo do contrato (`@Max(200)`). Acima disso a coluna volta a 200 linhas
 * e o "Ver mais" continua de onde parou.
 */
export const PRIORITY_KANBAN_MAX_KEPT_PAGES = Math.floor(
  PRIORITY_ROWS_PAGE_LIMIT / PRIORITY_KANBAN_COLUMN_PAGE_SIZE
)

const intersect = <T>(group: T[], screen: T[]): T[] =>
  screen.length ? group.filter((value) => screen.includes(value)) : [...group]

/**
 * Filtro do grupo da coluna somado ao filtro da tela. `undefined` = a coluna
 * não tem filtro no servidor ("Sem SLA"); `null` = o filtro da tela exclui o
 * grupo inteiro (nada a buscar).
 */
export function priorityColumnQueryFilter(
  column: PriorityGroupColumn,
  filters: PriorityPaymentsFilters
): PriorityColumnServerFilter | null | undefined {
  const group = column.serverFilter
  if (!group) return undefined
  if (group.slaState) {
    const slaState = intersect(group.slaState, filters.slaStates)
    return slaState.length ? { slaState } : null
  }
  if (group.situations) {
    const situations = intersect(group.situations, filters.situations)
    return situations.length ? { situations } : null
  }
  return undefined
}

/**
 * Total REAL da coluna pelos contadores do servidor: `slaCounters.byState`
 * (ignora o próprio filtro de estado — daí a interseção) ou a soma de
 * `counters.bySituation` (ignora o próprio filtro de situação). `undefined`
 * quando não há contador (BE sem `slaCounters` ou coluna sem filtro).
 */
export function priorityColumnTotal(
  column: PriorityGroupColumn,
  response: PriorityPaymentsResponse,
  filters: PriorityPaymentsFilters
): number | undefined {
  const filter = priorityColumnQueryFilter(column, filters)
  if (filter === undefined) return undefined
  if (column.serverFilter?.slaState) {
    const byState = response.slaCounters?.byState
    if (!byState) return undefined
    return (filter?.slaState ?? []).reduce((sum, state) => sum + (byState[state] ?? 0), 0)
  }
  const bySituation = response.counters?.bySituation as Partial<Record<string, number>> | undefined
  if (!bySituation) return undefined
  return (filter?.situations ?? []).reduce(
    (sum, situation) => sum + (bySituation[situation] ?? 0),
    0
  )
}
